import axios from 'axios';
import { AddressSearchObject } from 'services/AddressSearchService';
import { DEFAULT_TIMEOUT } from './index';

// Set the Azure Maps base URL
const AZURE_MAP_BASE_URL = 'https://atlas.microsoft.com';

// Create a new axios instance for the Azure Maps API
const azureMapApi = axios.create({
  baseURL: AZURE_MAP_BASE_URL,
  timeout: DEFAULT_TIMEOUT
});

// Define enums for entity type and query key
export enum EntityType {
  Geography = 'Geography',
  Municipality = 'Municipality',
  Street = 'Street',
  PointAddress = 'Point Address'
}

export enum QueryKey {
  ApiVersion = 'api-version',
  SubscriptionKey = 'subscription-key',
  EntityType = 'entityType',
  CountryCode = 'countryCode',
  PostalCode = 'postalCode',
  Language = 'language',
  CountrySet = 'countrySet',
  StreetName = 'streetName',
  Municipality = 'municipality'
}

// Define a class for the Azure Maps API client
export class AzureMapClient {
  // Define static properties for the API version, country code, and subscription key
  static apiVersion = '1.0';
  static countryCode = 'DE';
  static subscriptionKey = '8kj92LDtKh0F73WYFE1UvvEmX1orjZk1q5c8cc7g7Tw';

  // Define a method to search for an address
  static searchAddress(address: AddressSearchObject, structured: boolean = false) {
    // If the search is structured, build a structured query URL
    // Otherwise, build a non-structured query URL
    return structured
      ? azureMapApi.get(
          `search/address/structured/json?${this.buildAccessQuery()}&${this.buildStructuredQuery(
            address
          )}`
        )
      : azureMapApi.get(
          `search/address/json?${this.buildAccessQuery()}&query=${this.buildQuery(address)}`
        );
  }

  // Define a private method to build the access query parameters
  private static buildAccessQuery() {
    // Build an array of access query parameters
    return (
      [
        this.buildQueryParam(QueryKey.ApiVersion, this.apiVersion),
        this.buildQueryParam(QueryKey.SubscriptionKey, this.subscriptionKey),
        this.buildQueryParam(QueryKey.Language, 'de-DE'),
        this.buildQueryParam(QueryKey.CountrySet, 'DE'),
        this.buildQueryParam(QueryKey.CountryCode, 'DE'),
        // "typeahead=true",
        'limit=20'
        // "extendedPostalCodesFor=PAD,Addr,Geo,Str",
      ]
        // Filter out empty parameters
        .filter((queryParam) => queryParam !== '')
        // Join the parameters with "&" to create the access query string
        .join('&')
    );
  }

  // Define a private method to build the structured query parameters
  private static buildStructuredQuery(address: AddressSearchObject) {
    // Build an array of structured query parameters
    return (
      [
        this.buildQueryParam(QueryKey.PostalCode, address.postalCode),
        this.buildQueryParam(QueryKey.Municipality, address.municipality),
        this.buildQueryParam(QueryKey.StreetName, address.streetName)
      ]
        // Filter out empty parameters
        .filter((queryParam) => queryParam !== '')
        // Join the parameters with "&" to create the structured query string
        .join('&')
    );
  }

  // Define a private method to build the non-structured query parameters
  private static buildQuery(address: AddressSearchObject) {
    // Build an array of non-structured query parameters
    return [address.municipality, address.postalCode, address.streetName, address.streetNumber]
      .filter((param) => param !== '')
      .join(' ');
  }

  private static buildQueryParam(queryKey: QueryKey, parameter?: string) {
    return parameter ? `${queryKey}=${parameter}` : '';
  }
}
